/**
 * API client — fetch wrapper with automatic access-token refresh.
 *
 * On a 401 for an authed request, it tries ONE silent refresh (deduped across
 * concurrent calls), then retries. If refresh fails, it clears the session and
 * flips the session-expired flag so the UI can prompt a re-login.
 */
import { i18n } from '@/lib/i18n'
import { useAuthStore } from '@/stores/authStore'
import { useSessionStore } from '@/stores/sessionStore'

const API_URL = (import.meta.env.VITE_API_URL as string | undefined) ?? 'http://localhost:4000'

export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    readonly messageKey: string,
    readonly issues?: unknown,
  ) {
    super(messageKey)
    this.name = 'ApiError'
  }
}

async function toApiError(res: Response): Promise<ApiError> {
  let code = 'internal'
  let messageKey = 'errors.generic'
  let issues: unknown
  try {
    const body = (await res.json()) as { code?: string; message_key?: string; issues?: unknown }
    code = body.code ?? code
    messageKey = body.message_key ?? messageKey
    issues = body.issues
  } catch {
    /* non-JSON error body */
  }
  return new ApiError(res.status, code, messageKey, issues)
}

interface RequestOptions {
  method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE'
  body?: unknown
  /** Attach the access token + attempt refresh on 401. Default true. */
  auth?: boolean
}

function doFetch(path: string, opts: RequestOptions, token: string | null): Promise<Response> {
  // Only declare a JSON content-type when we actually send a body. A body-less
  // POST (resend-verify-email, 2fa/enroll, kyc/start, …) with an empty body but
  // `Content-Type: application/json` makes Fastify's JSON parser reject it with
  // FST_ERR_CTP_EMPTY_JSON_BODY (400) before the handler runs.
  const hasBody = opts.body !== undefined
  return fetch(`${API_URL}${path}`, {
    method: opts.method ?? 'GET',
    headers: {
      ...(hasBody ? { 'Content-Type': 'application/json' } : {}),
      'Accept-Language': i18n.language,
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    ...(hasBody ? { body: JSON.stringify(opts.body) } : {}),
  })
}

let refreshPromise: Promise<boolean> | null = null

async function tryRefresh(): Promise<boolean> {
  const { refreshToken } = useAuthStore.getState()
  if (!refreshToken) return false
  refreshPromise ??= fetch(`${API_URL}/auth/refresh`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ refresh_token: refreshToken }),
  })
    .then(async (res) => {
      if (!res.ok) return false
      const data = (await res.json()) as { access_token: string; refresh_token: string }
      useAuthStore.getState().setTokens(data.access_token, data.refresh_token)
      return true
    })
    .catch(() => false)
    .finally(() => {
      refreshPromise = null
    })
  return refreshPromise
}

export async function apiRequest<T>(path: string, opts: RequestOptions = {}): Promise<T> {
  const auth = opts.auth ?? true
  let token = auth ? useAuthStore.getState().accessToken : null
  let res = await doFetch(path, opts, token)

  if (res.status === 401 && auth) {
    const refreshed = await tryRefresh()
    if (refreshed) {
      token = useAuthStore.getState().accessToken
      res = await doFetch(path, opts, token)
    } else {
      useAuthStore.getState().clear()
      useSessionStore.getState().expire()
      throw await toApiError(res)
    }
  }

  if (!res.ok) throw await toApiError(res)
  if (res.status === 204) return undefined as T
  return (await res.json()) as T
}

/** Authenticated download (e.g. CSV export) — returns the raw Blob. */
export async function apiDownload(path: string): Promise<Blob> {
  const token = useAuthStore.getState().accessToken
  const res = await fetch(`${API_URL}${path}`, {
    headers: {
      'Accept-Language': i18n.language,
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
  })
  if (!res.ok) throw await toApiError(res)
  return res.blob()
}

/** Authenticated multipart upload (FormData). Don't set Content-Type — the
 *  browser adds the multipart boundary itself. */
export async function apiUpload<T>(path: string, form: FormData): Promise<T> {
  const token = useAuthStore.getState().accessToken
  const res = await fetch(`${API_URL}${path}`, {
    method: 'POST',
    headers: {
      'Accept-Language': i18n.language,
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: form,
  })
  if (!res.ok) throw await toApiError(res)
  if (res.status === 204) return undefined as T
  return (await res.json()) as T
}
